import { motion } from "motion/react";
import { useState } from "react";
import Toast from "./Toast";
import { 
  Shield, 
  Database, 
  Globe, 
  Info, 
  RotateCcw, 
  Upload, 
  Trash2 
} from "lucide-react";

interface SettingsViewProps {
  notificationCount?: number;
  onNotificationClick?: () => void;
}

export default function SettingsView({ notificationCount = 0, onNotificationClick }: SettingsViewProps) {
  const [showToast, setShowToast] = useState<{ message: string; type: "success" | "info" } | null>(null);
  const [settings, setSettings] = useState({
    notifications: {
      batchApprovals: true,
      deliveryAlerts: true,
      testReminders: true,
      weatherAlerts: false,
    },
    display: {
      darkMode: false,
      compactView: false,
    },
    quality: {
      autoApprove: false,
      strictMode: true,
    },
  });

  const toggleSetting = (category: keyof typeof settings, key: string) => {
    setSettings({
      ...settings,
      [category]: {
        ...settings[category],
        [key]: !settings[category][key as keyof typeof settings[typeof category]],
      },
    });
    setShowToast({ message: "Setting updated successfully", type: "success" });
  };

  const handleResetSettings = () => {
    setSettings({
      notifications: {
        batchApprovals: true,
        deliveryAlerts: true,
        testReminders: true,
        weatherAlerts: false,
      },
      display: {
        darkMode: false,
        compactView: false,
      },
      quality: {
        autoApprove: false,
        strictMode: true,
      },
    });
    setShowToast({ message: "Settings reset to defaults", type: "success" });
  };

  const handleExportSettings = () => {
    setShowToast({ message: "Exporting settings...", type: "info" });
    setTimeout(() => {
      setShowToast({ message: "Settings exported successfully", type: "success" });
    }, 1000);
  };

  const handleClearCache = () => {
    setShowToast({ message: "Clearing cache...", type: "info" });
    setTimeout(() => {
      setShowToast({ message: "Cache cleared successfully", type: "success" });
    }, 1000);
  };

  return (
    <div className="pb-24">
      {/* Active Notifications Banner */}
      {notificationCount > 0 && (
        <div className="px-5 mt-5 mb-3">
          <motion.button
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            whileTap={{ scale: 0.98 }}
            onClick={onNotificationClick}
            className="w-full bg-gradient-to-r from-[#FEF3C7] to-[#FED7AA] p-4 rounded-2xl flex items-center gap-3 cursor-pointer"
            style={{
              boxShadow: "0 2px 8px rgba(245, 158, 11, 0.15)",
            }}
          >
            <div className="relative">
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center"
                style={{
                  backgroundColor: "#F59E0B",
                  boxShadow: "0 2px 8px rgba(245, 158, 11, 0.3)",
                }}
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path
                    d="M15 6.66667C15 5.34058 14.4732 4.06881 13.5355 3.13113C12.5979 2.19345 11.3261 1.66667 10 1.66667C8.67392 1.66667 7.40215 2.19345 6.46447 3.13113C5.52678 4.06881 5 5.34058 5 6.66667C5 12.5 2.5 14.1667 2.5 14.1667H17.5C17.5 14.1667 15 12.5 15 6.66667Z"
                    stroke="white"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M11.4417 17.5C11.2952 17.7526 11.0849 17.9622 10.8319 18.1079C10.5789 18.2537 10.292 18.3304 10 18.3304C9.70802 18.3304 9.42115 18.2537 9.16814 18.1079C8.91513 17.9622 8.70484 17.7526 8.55835 17.5"
                    stroke="white"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <div
                className="absolute -top-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center"
                style={{
                  backgroundColor: "white",
                  boxShadow: "0 2px 6px rgba(0, 0, 0, 0.15)",
                }}
              >
                <span
                  style={{
                    fontSize: "10px",
                    fontWeight: "800",
                    color: "#F59E0B",
                  }}
                >
                  {notificationCount > 9 ? "9+" : notificationCount}
                </span>
              </div>
            </div>
            <div className="flex-1">
              <div
                className="text-[#92400E] mb-0.5"
                style={{ fontSize: "15px", fontWeight: "700" }}
              >
                {notificationCount} Active Notification{notificationCount !== 1 ? "s" : ""}
              </div>
              <div
                className="text-[#92400E]"
                style={{ fontSize: "12px", fontWeight: "500", opacity: 0.8 }}
              >
                You have unread notifications waiting
              </div>
            </div>
            <motion.div
              animate={{ scale: [1, 1.1, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              <svg width="6" height="6" viewBox="0 0 6 6" fill="none">
                <circle cx="3" cy="3" r="3" fill="#F59E0B" />
              </svg>
            </motion.div>
          </motion.button>
        </div>
      )}

      {/* Notification Settings */}
      <div className="px-5 mb-5 mt-5">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white rounded-3xl p-5"
          style={{
            boxShadow:
              "0 2px 4px rgba(0, 0, 0, 0.02), 0 8px 24px rgba(0, 0, 0, 0.06)",
          }}
        >
          <div
            className="text-[#717182] mb-5"
            style={{
              fontSize: "11px",
              letterSpacing: "0.05em",
              fontWeight: "600",
            }}
          >
            NOTIFICATIONS
          </div>

          <div className="space-y-4">
            {[
              {
                key: "batchApprovals",
                label: "Batch Approvals",
                desc: "Get notified when batches are approved",
              },
              {
                key: "deliveryAlerts",
                label: "Delivery Alerts",
                desc: "Real-time delivery status updates",
              },
              {
                key: "testReminders",
                label: "Test Reminders",
                desc: "Scheduled test notifications",
              },
              {
                key: "weatherAlerts",
                label: "Weather Alerts",
                desc: "Weather condition warnings",
              },
            ].map((item) => (
              <div
                key={item.key}
                className="flex items-center justify-between"
              >
                <div className="flex-1">
                  <div
                    className="text-[#0A0F1C] mb-1"
                    style={{ fontSize: "15px", fontWeight: "600" }}
                  >
                    {item.label}
                  </div>
                  <div
                    className="text-[#717182]"
                    style={{ fontSize: "12px" }}
                  >
                    {item.desc}
                  </div>
                </div>
                <motion.button
                  whileTap={{ scale: 0.95 }}
                  onClick={() => toggleSetting("notifications", item.key)}
                  className="relative w-12 h-7 rounded-full transition-all"
                  style={{
                    backgroundColor: settings.notifications[
                      item.key as keyof typeof settings.notifications
                    ]
                      ? "#1B2A5B"
                      : "#E5E7EB",
                    boxShadow: settings.notifications[
                      item.key as keyof typeof settings.notifications
                    ]
                      ? "0 2px 8px rgba(27, 42, 91, 0.3)"
                      : "inset 0 1px 2px rgba(0, 0, 0, 0.1)",
                  }}
                >
                  <motion.div
                    className="absolute top-1 w-5 h-5 rounded-full bg-white"
                    style={{
                      boxShadow: "0 1px 3px rgba(0, 0, 0, 0.2)",
                    }}
                    animate={{
                      left: settings.notifications[
                        item.key as keyof typeof settings.notifications
                      ]
                        ? "26px"
                        : "4px",
                    }}
                    transition={{ type: "spring", stiffness: 500, damping: 30 }}
                  />
                </motion.button>
              </div>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Display Settings */}
      <div className="px-5 mb-5">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.5 }}
          className="bg-white rounded-3xl p-5"
          style={{
            boxShadow:
              "0 2px 4px rgba(0, 0, 0, 0.02), 0 8px 24px rgba(0, 0, 0, 0.06)",
          }}
        >
          <div
            className="text-[#717182] mb-5"
            style={{
              fontSize: "11px",
              letterSpacing: "0.05em",
              fontWeight: "600",
            }}
          >
            DISPLAY
          </div>

          <div className="space-y-4">
            {[
              {
                key: "darkMode",
                label: "Dark Mode",
                desc: "Use dark theme",
              },
              {
                key: "compactView",
                label: "Compact View",
                desc: "Show more data on screen",
              },
            ].map((item) => (
              <div
                key={item.key}
                className="flex items-center justify-between"
              >
                <div className="flex-1">
                  <div
                    className="text-[#0A0F1C] mb-1"
                    style={{ fontSize: "15px", fontWeight: "600" }}
                  >
                    {item.label}
                  </div>
                  <div
                    className="text-[#717182]"
                    style={{ fontSize: "12px" }}
                  >
                    {item.desc}
                  </div>
                </div>
                <motion.button
                  whileTap={{ scale: 0.95 }}
                  onClick={() => toggleSetting("display", item.key)}
                  className="relative w-12 h-7 rounded-full transition-colors"
                  style={{
                    backgroundColor: settings.display[
                      item.key as keyof typeof settings.display
                    ]
                      ? "#1B2A5B"
                      : "#E5E7EB",
                  }}
                >
                  <motion.div
                    className="absolute top-1 w-5 h-5 rounded-full bg-white"
                    style={{
                      boxShadow: "0 1px 3px rgba(0, 0, 0, 0.2)",
                    }}
                    animate={{
                      left: settings.display[
                        item.key as keyof typeof settings.display
                      ]
                        ? "26px"
                        : "4px",
                    }}
                    transition={{ type: "spring", stiffness: 500, damping: 30 }}
                  />
                </motion.button>
              </div>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Quality Settings */}
      <div className="px-5 mb-5">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.5 }}
          className="bg-white rounded-3xl p-5"
          style={{
            boxShadow:
              "0 2px 4px rgba(0, 0, 0, 0.02), 0 8px 24px rgba(0, 0, 0, 0.06)",
          }}
        >
          <div
            className="text-[#717182] mb-5"
            style={{
              fontSize: "11px",
              letterSpacing: "0.05em",
              fontWeight: "600",
            }}
          >
            QUALITY CONTROL
          </div>

          <div className="space-y-4">
            {[
              {
                key: "autoApprove",
                label: "Auto-Approve",
                desc: "Automatically approve passing batches",
              },
              {
                key: "strictMode",
                label: "Strict Mode",
                desc: "Apply stricter quality thresholds",
              },
            ].map((item) => (
              <div
                key={item.key}
                className="flex items-center justify-between"
              >
                <div className="flex-1">
                  <div
                    className="text-[#0A0F1C] mb-1"
                    style={{ fontSize: "15px", fontWeight: "600" }}
                  >
                    {item.label}
                  </div>
                  <div
                    className="text-[#717182]"
                    style={{ fontSize: "12px" }}
                  >
                    {item.desc}
                  </div>
                </div>
                <motion.button
                  whileTap={{ scale: 0.95 }}
                  onClick={() => toggleSetting("quality", item.key)}
                  className="relative w-12 h-7 rounded-full transition-colors"
                  style={{
                    backgroundColor: settings.quality[
                      item.key as keyof typeof settings.quality
                    ]
                      ? "#1B2A5B"
                      : "#E5E7EB",
                  }}
                >
                  <motion.div
                    className="absolute top-1 w-5 h-5 rounded-full bg-white"
                    style={{
                      boxShadow: "0 1px 3px rgba(0, 0, 0, 0.2)",
                    }}
                    animate={{
                      left: settings.quality[
                        item.key as keyof typeof settings.quality
                      ]
                        ? "26px"
                        : "4px",
                    }}
                    transition={{ type: "spring", stiffness: 500, damping: 30 }}
                  />
                </motion.button>
              </div>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Other Options */}
      <div className="px-5 mb-5">
        <div className="space-y-3">
          {[
            { icon: <Shield size={20} className="text-[#1B2A5B]" />, label: "Security", desc: "Password & authentication" },
            { icon: <Database size={20} className="text-[#1B2A5B]" />, label: "Data & Storage", desc: "Manage app data" },
            { icon: <Globe size={20} className="text-[#1B2A5B]" />, label: "Language & Region", desc: "English (US)" },
            { icon: <Info size={20} className="text-[#1B2A5B]" />, label: "About", desc: "Version 2.4.1" },
          ].map((item, index) => (
            <motion.button
              key={item.label}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 + index * 0.05, duration: 0.5 }}
              whileTap={{ scale: 0.98 }}
              className="w-full bg-white p-5 rounded-2xl text-left flex items-center gap-4"
              style={{
                boxShadow: "0 1px 3px rgba(0, 0, 0, 0.04), 0 8px 16px rgba(0, 0, 0, 0.02)",
              }}
            >
              <div 
                className="w-10 h-10 rounded-xl flex items-center justify-center"
                style={{
                  backgroundColor: "#F9FAFB",
                }}
              >
                {item.icon}
              </div>
              <div className="flex-1">
                <div
                  className="text-[#0A0F1C] mb-0.5"
                  style={{ fontSize: "15px", fontWeight: "600" }}
                >
                  {item.label}
                </div>
                <div
                  className="text-[#717182]"
                  style={{ fontSize: "13px" }}
                >
                  {item.desc}
                </div>
              </div>
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                className="text-[#717182]"
              >
                <path
                  d="M7 4l6 6-6 6"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </motion.button>
          ))}
        </div>
      </div>

      {/* Additional Actions */}
      <div className="px-5 mb-5">
        <div className="space-y-3">
          <motion.button
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3, duration: 0.5 }}
            whileTap={{ scale: 0.98 }}
            className="w-full bg-white p-5 rounded-2xl text-left flex items-center gap-4"
            style={{
              boxShadow: "0 1px 3px rgba(0, 0, 0, 0.04), 0 8px 16px rgba(0, 0, 0, 0.02)",
            }}
            onClick={handleResetSettings}
          >
            <div 
              className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{
                backgroundColor: "#F9FAFB",
              }}
            >
              <RotateCcw size={20} className="text-[#1B2A5B]" />
            </div>
            <div className="flex-1">
              <div
                className="text-[#0A0F1C] mb-0.5"
                style={{ fontSize: "15px", fontWeight: "600" }}
              >
                Reset Settings
              </div>
              <div
                className="text-[#717182]"
                style={{ fontSize: "13px" }}
              >
                Revert to default settings
              </div>
            </div>
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              className="text-[#717182]"
            >
              <path
                d="M7 4l6 6-6 6"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </motion.button>
          <motion.button
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.35, duration: 0.5 }}
            whileTap={{ scale: 0.98 }}
            className="w-full bg-white p-5 rounded-2xl text-left flex items-center gap-4"
            style={{
              boxShadow: "0 1px 3px rgba(0, 0, 0, 0.04), 0 8px 16px rgba(0, 0, 0, 0.02)",
            }}
            onClick={handleExportSettings}
          >
            <div 
              className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{
                backgroundColor: "#F9FAFB",
              }}
            >
              <Upload size={20} className="text-[#1B2A5B]" />
            </div>
            <div className="flex-1">
              <div
                className="text-[#0A0F1C] mb-0.5"
                style={{ fontSize: "15px", fontWeight: "600" }}
              >
                Export Settings
              </div>
              <div
                className="text-[#717182]"
                style={{ fontSize: "13px" }}
              >
                Save current settings to a file
              </div>
            </div>
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              className="text-[#717182]"
            >
              <path
                d="M7 4l6 6-6 6"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </motion.button>
          <motion.button
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4, duration: 0.5 }}
            whileTap={{ scale: 0.98 }}
            className="w-full bg-white p-5 rounded-2xl text-left flex items-center gap-4"
            style={{
              boxShadow: "0 1px 3px rgba(0, 0, 0, 0.04), 0 8px 16px rgba(0, 0, 0, 0.02)",
            }}
            onClick={handleClearCache}
          >
            <div 
              className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{
                backgroundColor: "#F9FAFB",
              }}
            >
              <Trash2 size={20} className="text-[#1B2A5B]" />
            </div>
            <div className="flex-1">
              <div
                className="text-[#0A0F1C] mb-0.5"
                style={{ fontSize: "15px", fontWeight: "600" }}
              >
                Clear Cache
              </div>
              <div
                className="text-[#717182]"
                style={{ fontSize: "13px" }}
              >
                Remove cached data
              </div>
            </div>
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              className="text-[#717182]"
            >
              <path
                d="M7 4l6 6-6 6"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </motion.button>
        </div>
      </div>

      {/* Toast */}
      {showToast && (
        <Toast
          message={showToast.message}
          type={showToast.type}
          onClose={() => setShowToast(null)}
        />
      )}
    </div>
  );
}